import type { CategoryContent } from '../row.js';

/**
 * Centraal-Aziatische geschiedenis, Oost-Aziatische ontwikkeling.
 *
 * Twee categorieën die het oorspronkelijke twaalftal slecht behandelde:
 * "Geschiedenis & beschavingen" betekende in de praktijk Europese en
 * mediterrane geschiedenis met een Zheng He-vraag voor de balans, wat een
 * echte dekkingsscheefheid is en geen afrondingsfout. Dit zijn afzonderlijke
 * pakketten omdat de specialistische literatuur ook gescheiden is.
 *
 * Calibratie zoals in `DIFFICULTY_TIERS`: graduate is masterniveau in het
 * vakgebied, phd is een specialist, professor volgt de actuele literatuur.
 */

export const CENTRAL_ASIA: CategoryContent = {
  bscba: [
    [
      'Welke Chinese dynastie was in de middeleeuwen de voornaamste concurrent voor invloed in Centraal-Azië?',
      ['De Tang', 'De Qin', 'De Song', 'De Yuan'],
      0,
      'De Tang-dynastie (618-907) was de belangrijkste Chinese macht in Centraal-Azië vóór de Mongoolse expansie.',
    ],
    [
      'Welk groot geloof verspreidde zich van Zuid-Azië oostwaarts door Centraal-Azië langs handelsroutes?',
      ['Boeddhisme', 'Islam', 'Christendom', 'Zoroastrianisme'],
      0,
      'Het boeddhisme reisde van India via Centraal-Azië naar Oost-Azië langs Zijderoutenetwerken.',
    ],
    [
      'Welk koopmansvolk wordt historisch het meest erkend voor het ontwikkelen van overland handelsnetwerken in Centraal-Azië?',
      ['De Sogdiërs', 'De Turkse volkeren', 'De Perziërs', 'De Arabieren'],
      0,
      'Sogdische kooplieden uit Samarkand en Boekhara domineerden langeafstandshandel in de regio.',
    ],
    [
      'Wat was het voornaamste economische systeem van middeleeuwse nomadische volkeren in Centraal-Azië?',
      ['Veeteelt', 'Landbouw', 'Zeehandel en visserij', 'Mijnbouw en metallurgie'],
      0,
      'Nomadische veehouderij van paarden, schapen en jakken vormde de economische basis van steppemaatschappijen.',
    ],
    [
      'Welke Mongoolse leider verenigde de verschillende stamconfederaties van de Centraal-Aziatische steppe?',
      ['Temüjin (Dzjengis Khan)', 'Batu Khan', 'Timoer', 'Koeblai Khan'],
      0,
      'Temüjin verenigde de Mongoolse stammen in het begin van de 13e eeuw en werd Dzjengis Khan.',
    ],
    [
      'Het Timoeridenrijk, dat Samarkand en Herat regeerde, was een belangrijke beschermheer van wat?',
      ['Kunst, architectuur en geleerdheid', 'Zeevaartonderzoek', 'Infanteriegerichte oorlogvoering', 'Landbouwinnovatie'],
      0,
      'Timoeridenheersers maakten hun hoofdsteden tot centra van astronomie, wiskunde en literatuur.',
    ],
    [
      'Welke middeleeuwse Centraal-Aziatische stad werd beroemd als belangrijk centrum van islamitisch leren?',
      ['Boekhara', 'Kashgar', 'Samarkand', 'Tasjkent'],
      0,
      'Boekhara was eeuwenlang een belangrijk knooppunt van islamitisch recht en wiskunde.',
    ],
    [
      'Waarom werd Centraal-Azië voor opeenvolgende rijken en mogendheden strategisch belangrijk?',
      ['Controle over handelsroutes tussen verre beschavingen creëerde rijkdom en politieke invloed', 'Enorme voorraden edelmetalen', 'Vruchtbaar landbouwland geschikt voor graanteelt', 'Toegang tot warmwaterhavens'],
      0,
      'De geografische positie tussen beschavingen maakte Centraal-Aziatische handelsroutes economisch vitaal.',
    ],
    [
      'In welke eeuw intensiveerde het Russische Rijk zijn expansie in Centraal-Azië?',
      ['De negentiende eeuw', 'De zestiende eeuw', 'De achttiende eeuw', 'De twintigste eeuw'],
      0,
      'De Russische keizerijkse expansie in Centraal-Azië versnelde aanzienlijk in de jaren 1800.',
    ],
    [
      'Wat waren de khanaten die Centraal-Azië in de vroegmoderne periode regeerden?',
      ['Onafhankelijke staten geregeerd door khans, met steden als Boekhara, Chiwa en Kokand als hoofdsteden', 'Provincies direct bestuurd door de Russische Tsar', 'Door het Ottomaanse Rijk gelicenteerde handelsvennootschappen', 'Nomadische confederaties zonder vaste stedelijke centra'],
      0,
      'Boekhara, Chiwa en Kokand waren onafhankelijke khanaten die Centraal-Azië regeerden tot de Russische verovering.',
    ],
    [
      'Welke bergketens vormden de oostelijke en zuidelijke grenzen van het historische Centraal-Azië?',
      ['De Pamir en Tian Shan', 'De Kaukasus en Karpaten', 'De Uralen en Altai', 'Alleen de Himalaya'],
      0,
      'De Pamir en Tian Shan creëerden natuurlijke barrières tussen Centraal-Azië en Oost-/Zuid-Azië.',
    ],
    [
      'Hoe werd islam de dominante religie van Centraal-Azië?',
      ['Door militaire verovering en daaropvolgende handelsnetwerken die de invloed van moslims vergrootten', 'Uitsluitend via vredige zendingswerk', 'Het was het inheems geloof van de regio vóór invasies', 'Door Europese koloniale expansie'],
      0,
      'Arabische veroveringen vanaf de 7e eeuw brachten islam; kooplieden breidden daarna de invloed van moslims uit.',
    ],
    [
      'Wat was het voornaamste doel van karavansera\'s langs de Zijderoute?',
      ['Kooplieden onderdak, veiligheid en handelsmogelijkheden bieden', 'Als militaire versterkingen dienen', 'Luxegoederen verwerken en verfijnen voor doorverkoop', 'Boeddhistische pelgrims huisvesten'],
      0,
      'Karavansera\'s waren versterkte wegkanten-herbergen die gastvrijheid en veiligheid voor kooplieden boden.',
    ],
    [
      'Na de val van Mongoolse eenheid, welke etnische en linguïstische groepen werden dominant in Centraal-Azië?',
      ['Turkse en Oezbeekse volkeren', 'Scythen en Sarmathen', 'Hunnen en Rouran', 'Franken en Germaanse stammen'],
      0,
      'Turkse en Oezbeekse groepen regeerden Centraal-Azië nadat de Mongoolse macht in regionale khanaten uiteenviel.',
    ],
    [
      'Welke grote geopolitieke competitie gaf vorm aan de geschiedenis van Centraal-Azië in de negentiende eeuw?',
      ['Britse en Russische keizerlijke belangen die om regionale dominantie wedijveren', 'Ottomaanse en Perzische dynastieke rivaliteit', 'Chinese en Mongoolse territoriale conflicten', 'Arabische en Joodse politieke geschillen'],
      0,
      'Groot-Brittannië en Rusland wedijverden om invloed in Centraal-Azië, het zogenoemde "Great Game".',
    ],
  ],
  msc: [
    [
      'De Slag bij Talas in 751 werd uitgevochten tussen Tang-troepen en welke macht?',
      ['Het Abbasidische kalifaat', 'Het Omajjadische kalifaat', 'Het Tibetaanse Rijk', 'Het Oeigoerse Khaganaat'],
      0,
      'Dit gebeurde drie jaar na de Abbasidische revolutie en markeerde de praktische grens van Tang-invloed ten westen van de Pamir, eerder dan een beslissende nederlaag.',
    ],
    [
      'Waarom zijn de Sogdiërs historisch vooral van belang?',
      [
        'Als het handelsnetwerk wiens taal de lingua franca van de Zijderoutehandel werd',
        'Als de dynastie die Transoxanië onder één bestuur verenigde',
        'Als de nomadenconfederatie die de Scythen van de steppe verdreef',
        'Als de kloosterorde die het boeddhisme naar China bracht',
      ],
      0,
      'Sogdische brieven en contracten uit Dunhuang en de berg Mugh zijn de reden dat we zoveel weten over de mechanismen van die handel.',
    ],
    [
      'Welke staat vernietigde Dzjengis Khan tijdens de veldtocht van 1219-1221?',
      [
        'Het Khwarezmische Rijk',
        'Het Ghuridische Sultanaat',
        'Het Seltsjoekse Sultanaat van Rum',
        'Het Samanidische Emiraat',
      ],
      0,
      'De aanleiding was de moord op een Mongoolse gezantschap in Otrar. Merv, Boekhara en Samarkand werden achtereenvolgens geplunderd.',
    ],
    [
      'In welke stad vestigde Timoer zijn hoofdstad?',
      ['Samarkand', 'Boekhara', 'Herat', 'Balkh'],
      0,
      'Zijn kleinzoon Ulugh Beg bouwde daar de sterrenwacht; Herat werd onder Shah Rukh de Timoeridische culturele hoofdstad.',
    ],
    [
      'De negentiende-eeuwse "Great Game" beschrijft rivaliteit tussen welke twee machten?',
      [
        'Groot-Brittannië en Rusland',
        'Rusland en Qing-China',
        'Groot-Brittannië en het Ottomaanse Rijk',
        'Rusland en Perzië',
      ],
      0,
      'De term werd populair gemaakt door Kipling; het historiografische debat gaat over hoeveel ervan strategische realiteit was en hoeveel werd geproduceerd door de mannen die de rapporten schreven.',
    ],
  ],
  phd: [
    [
      'De Kara-Khitai (Westelijke Liao) werd gesticht door vluchtelingen na de val van welk rijk?',
      [
        'De Khitaanse Liao-dynastie, na haar verovering door de Jurchen Jin',
        'De Tangut Westelijke Xia, na de Mongoolse invasies',
        'Het Oeigoerse Khaganaat, na zijn nederlaag tegen de Jenisej-Kirgiezen',
        'De Noordelijke Song, na het verlies van Kaifeng',
      ],
      0,
      'Yelü Dashi leidde hen in de jaren 1120 naar het westen. Hun overwinning op de Seltsjoeken bij Qatwan in 1141 is een van de mogelijke bronnen voor de Prester John-legende.',
    ],
    [
      'Wat was de yasa?',
      [
        'Het geheel van Mongools recht en gewoonte dat aan Dzjengis Khan wordt toegeschreven',
        'De belasting die Mongoolse bestuurders aan sedentaire bevolkingen oplegden',
        'Het postrelaisysteem dat de Mongoolse leengebieden met elkaar verbond',
        'De raad van prinsen die een nieuwe khan koos',
      ],
      0,
      'Er is geen complete tekst overgeleverd, waardoor zowel de inhoud als het bestaan als geschreven wetboek omstreden zijn. Het relaisysteem is de yam; de raad is de quriltai.',
    ],
    [
      'Hoe wordt de Jadid-beweging in Russisch Centraal-Azië het best gekarakteriseerd?',
      [
        'Als een islamitisch-modernistische hervormingsbeweging rond nieuw-methodisch onderwijs en gedrukte journalistiek',
        'Als een soefistisch revivalisme-netwerk dat Russische bestuurlijke penetratie weerstond',
        'Als een pan-Turkse politieke partij die aansluiting bij het Ottomaanse Rijk zocht',
        'Als een clericale factie die het madrasa-curriculum tegen hervorming verdedigde',
      ],
      0,
      'Genoemd naar usul-i jadid, "de nieuwe methode". De Sovjetstaat coöpteerde de Jadids eerst en vernietigde ze vervolgens tijdens de zuiveringen.',
    ],
    [
      'Wat was de Basmachi-beweging?',
      [
        'Gewapend verzet tegen de bolsjewistische en vroege Sovjetheerschappij in heel Turkestan',
        'Een Kazachse autonomistische beweging die onderhandelde met de Voorlopige Regering',
        'Een confederatie van Turkmeense stammen die zich verzette tegen de Russische verovering in de jaren 1880',
        'Een hervormingsgezinde factie binnen het emiraat Boekhara die een grondwet nastreefde',
      ],
      0,
      'De naam komt van een pejoratief dat "bandiet" betekent. De beweging liep van ongeveer 1916 tot begin jaren 1930 en was eerder een reeks lokale opstanden dan één beweging.',
    ],
    [
      'Door wie werd het Zunghaarse Khanaat in de jaren 1750 vernietigd?',
      [
        'De Qing onder de Qianlong-keizer',
        'Het Russische Rijk onder Elizabeth',
        'De Kazachse Middenhorde',
        'Het Durrani-Rijk onder Ahmad Shah',
      ],
      0,
      'De veldtocht en de daaropvolgende demografische ramp creëerden wat de Qing daarna Xinjiang noemde - "de nieuwe grens".',
    ],
  ],
  professor: [
    [
      'Wat concludeerde de genomische studie van 2021 naar de mummies uit de bronstijd in het Tarimbekken?',
      [
        'Ze stammen grotendeels af van een lokale Ancient North Eurasian-populatie, genetisch geïsoleerd maar cultureel verbonden',
        'Het waren Indo-Europese migranten uit de Afanasievo-cultuur van het Altaigebergte, die met wielvoertuigen en zuivelbewerking aankwamen',
        'Ze stammen voornamelijk af van Bactria-Margiana-landbouwers die naar het noorden trokken',
        'Ze vertegenwoordigen een vroege vermenging van Yamnaya-steppeherders met Oost-Aziatische boeren',
      ],
      0,
      'Zhang et al., Nature. Dit ontkrachtte de migratiehypotheses die domineerden sinds de mummies bekend werden, en scheidde genetische afkomst netjes van culturele overdracht.',
    ],
    [
      'Welke figuren worden herdacht in de Orkhon-inscripties?',
      [
        'Bilge Khagan en zijn broer Kul Tigin van het Tweede Türk-Khaganaat',
        'Bumin Qaghan en Istämi, stichters van het Eerste Türk-Khaganaat',
        'Kutlug Bilge Köl en de stichters van het Oeigoerse Khaganaat',
        'Tonyukuk alleen, als vizier van drie opeenvolgende khagans',
      ],
      0,
      'De oudste substantiële Turkse teksten, in het runiforme schrift, en ongewoon omdat het het eigen verhaal van een steppestaat is in plaats van een Chinese kroniek.',
    ],
    [
      'Wat betekende Sovjet-korenizatsija in Centraal-Azië in de praktijk?',
      [
        'Het promoveren van titulaire nationaliteiten naar partij- en staatsfuncties en het opbouwen van instellingen in de eigen taal',
        'Het vestigen van Slavische bevolkingsgroepen in de republieken om lokale meerderheden te verzwakken',
        'Het afschaffen van de republieken ten gunste van direct bestuur vanuit Moskou',
        'Het herstellen van vooroorlogs gewoonterecht onder Sovjettoezicht, in plaats van zowel sharia als koloniale rechtbanken',
      ],
      0,
      'Het revisionistische argument - Martin, Hirsch - is dat de Sovjetstaat de nationale categorieën die hij vervolgens bestuurde actief construeerde, in plaats van ze slechts te erkennen.',
    ],
    [
      'Welke twee khanaten onderwierp Nader Shahs veldtocht ten noorden van de Amu Darja in 1740?',
      ['Boekhara en Chiwa', 'Kokand en Boekhara', 'Chiwa en Kashgar', 'Balkh en Badakhshan'],
      0,
      'Beide werden tot schatplichtige status teruggebracht; het herstel van de Oezbeekse khanaten na zijn moord in 1747 bepaalde de regio tot de Russische verovering.',
    ],
    [
      'Welk historiografisch argument wordt aangevoerd in Beckwiths "Empires of the Silk Road"?',
      [
        'Dat Centraal-Eurazië op zichzelf een economisch en politiek centrum vormde',
        'Dat steppe-rijken fundamenteel parasitair waren op sedentair landbouwoverschot',
        'Dat de Zijderoute als continue handelsroute een negentiende-eeuwse uitvinding is zonder premoderne realiteit',
        'Dat klimaatvariabiliteit de belangrijkste drijfveer is voor de vorming van steppe-imperia',
      ],
      0,
      'Het anti-perifere argument. De stelling dat "Zijderoute" zelf een moderne term is - von Richthofen, 1877 - is waar en wordt afzonderlijk beargumenteerd, met name door Hansen.',
    ],
  ],
};

/**
 * Strikt beperkt tot economische/ontwikkelingsinhoud uit 1980-2000 - alles
 * wat algemener was (theorie zonder datum, of gedateerd buiten dat venster)
 * is verhuisd naar de nieuwe categorie Oost-Aziatische geschiedenis
 * (asia2.ts). Zie MORE_EAST_ASIA in more/eastasia.ts voor de rest van deze
 * categorie.
 */
export const EAST_ASIA: CategoryContent = {
  bscba: [
    [
      'Welke term beschrijft een regering die actief economische ontwikkeling stuurt via strategische planning en gerichte investeringen?',
      ['Ontwikkelingsstaat', 'Centraal geplande economie', 'Laissez-faire-kapitalisme', 'Feodale economie'],
      0,
      'De ontwikkelingsstaat gebruikte selectieve investeringen om snelle industriële groei in Oost-Azië te bevorderen.',
    ],
    [
      'Wat waren zaibatsu in naoorlogs Japan?',
      ['Grote familiegecontroleerde bedrijfsconcerns die over meerdere sectoren waren gediversifieerd', 'Door de regering eigendom fabrieken', 'Vakbonden in zware industrie', 'Landbouwcoöperatieven voor marketing'],
      0,
      'Zaibatsu waren massieve concerns als Mitsubishi en Mitsui die Japan domineerden.',
    ],
    [
      'Waarom benadrukten Zuid-Korea en Taiwan exportgestuurde groeistrategieën in de jaren 1960-1980?',
      ['Kleine binnenlandse markten maakten buitenlandse verkoop essentieel om valuta voor ontwikkeling te verkrijgen', 'Overvloedige natuurlijke hulpbronnen voor internationale verkoop', 'Regeringsmandaat om alle import uit te roeien', 'Koloniaal erfgoed vereist specifieke handelspartners'],
      0,
      'Exportverkoop genereerde de vreemde valuta die nodig was voor industriële investeringen in deze kleine markten.',
    ],
    [
      'Welke vier Oost- en Zuidoost-Aziatische economieën werden in de jaren 1980 bekend als "Tijger"- of "Draak"-economieën?',
      ['Zuid-Korea, Taiwan, Hongkong en Singapore', 'Japan, Thailand, Maleisië en Indonesië', 'China, Vietnam, Filipijnen en Cambodja', 'Vietnam, Laos, Myanmar en Thailand'],
      0,
      'De Vier Tijgers bereikten snelle groei via exportfabricage en buitenlandse investeringen.',
    ],
    [
      'Wat droeg directe buitenlandse investeringen (DBI) bij aan Oost-Aziatische landen tijdens snelle industrialisering?',
      ['Kapitaal, fabricagetechnologie en managementdeskundigheid', 'Alleen lageloonarbeidskansen', 'Koloniale politieke controle en schuld', 'Milieuschade zonder economisch voordeel'],
      0,
      'DBI verschafte kapitaal en technologie die de verschuiving naar exportfabricage mogelijk maakten.',
    ],
    [
      'Wat beoogde Chinas "hervormings- en openingsbeleid" dat in 1978 begon te bereiken?',
      ['Geleidelijke invoering van marktmechanismen bij opening voor buitenlandse investeringen', 'Onmiddellijke verwerping van alle communistische structuren', 'Terugkeer van de economie van industrie naar landbouw', 'Verdere isolatie van wereldeconomische systemen'],
      0,
      'De hervormingen voerden geleidelijk marktmechanismen in terwijl staatscontrole behouden bleef.',
    ],
    [
      'Wat waren speciale economische zones (SEZ) in China?',
      ['Aangewezen kustgebieden met gunstig beleid om buitenlandse investeringen en fabricage aan te trekken', 'Militaire gesloten zones voor buitenlanders', 'Landbouw- en innovatiegebieden', 'Historische en culturele bewaringssites'],
      0,
      'SEZ\'s als Shenzhen boden belastingvoordelen aan om multinationale fabricage aan te trekken.',
    ],
    [
      'Wat introduceerde Vietnams "Doi Moi" (Vernieuwing) hervormingsbeleid van 1986?',
      ['Marktmechanismen en beperkte private bedrijven terwijl de Communistische Partij politieke controle behield', 'Volledige overgang naar Westerse representatieve democratie', 'Grotere economische isolatie van de wereldmarkt', 'Uitbreiding van centraal geplande socialistische productie'],
      0,
      'Doi Moi introduceerde markthervorming vergelijkbaar met Chinas economische opening.',
    ],
    [
      'Welk land werd in de jaren 1980 \'s werelds grootste exporteur van elektronica en halfgeleiders?',
      ['Japan', 'Zuid-Korea', 'Duitsland', 'Taiwan'],
      0,
      'Japan domineerde wereldwijd elektronicafabricage in de jaren 1980.',
    ],
    [
      'Welke strategie gebruikten Maleisië en Thailand om multinationale fabricageinvesteringen aan te trekken?',
      ['Lage arbeidskosten, politieke stabiliteit en toegang tot natuurlijke hulpbronnen bieden', 'Onafhankelijk inheemse technologiebedrijven bouwen', 'Buitenlandse eigendom strikt beperken om lokale industrieën te beschermen', 'Uitsluitend vertrouwen op landbouw- en grondstoffenexport'],
      0,
      'Lage lonen en stabiliteit trokken buitenlandse fabrikanten naar de regio.',
    ],
    [
      'Hoe beïnvloedde snelle industrialisering in Oost-Azië de plattelandsbevolking?',
      ['Plattelandsarbeidskrachten migreerden naar steden op zoek naar fabrieksbanen', 'Plattelandsgebieden werden zware industriecentra', 'Boerderijen werden productiever door alleen mechanisering', 'Stedelijke bevolking daalde omdat arbeidskrachten naar landbouw terugkeerden'],
      0,
      'Plattelandsarbeidskrachten migreerden naar steden voor fabriekswerk en hogere lonen.',
    ],
    [
      'Welke rol speelden Japanse bedrijven in Oost-Aziatische industrialisatienetwerken?',
      ['Ze investeerden in joint ventures, droegen technologie over en trainden lokale arbeidskrachten in de regio', 'Ze hielden alle geavanceerde technologie en voorkwamen andermans toegang', 'Ze vermeden investeringen buiten Japan', 'Ze exploiteerden hulpbronnen terwijl lokale industriële ontwikkeling werd verhinderd'],
      0,
      'Japanse bedrijven investeerden regionaal via joint ventures en technologielicenties.',
    ],
    [
      'Waarom was infrastructuurinvestering cruciaal voor Oost-Aziatische ontwikkeling in deze periode?',
      ['Havens, wegen en energiesystemen maakten vervaardigings- en handelsnetwerken essentieel voor groei mogelijk', 'Infrastructuur was verspillend en vertragde ontwikkeling', 'Het diende vooral militaire doeleinden en strategie', 'Het werd door Westerse koloniale mogendheden tegen plaatselijk belang opgelegd'],
      0,
      'Havens, wegen en elektriciteitsnetten maakten vervaardigings- en exporthandel mogelijk.',
    ],
    [
      'Wat gebeurde er met de rol van landbouw in snel industrialiserende Oost-Aziatische economieën?',
      ['Het aandeel van landbouwproductie in het BBP daalde naarmate economieën verschoven naar fabricage en diensten', 'Het economisch belang van landbouw bleef stabiel', 'Landbouwproductie nam toe zonder verschuiving naar industrie', 'Landbouw verdween volledig uit deze economieën'],
      0,
      'Het aandeel van landbouw in het BBP daalde naarmate vervaardiging en diensten groeiden.',
    ],
    [
      'Welke internationale instelling hielp infrastructuur en ontwikkelingsprojecten in Oost-Azië financieren?',
      ['De Aziatische Ontwikkelingsbank en Wereldbank', 'OPEC', 'De Sovjet-Comecon-bloc', 'De Europese Unie'],
      0,
      'De Aziatische Ontwikkelingsbank financierde infrastructuur in Oost-Azië.',
    ],
  ],
  msc: [
    [
      'Wat beschrijft het Japanse "hoofdbanksysteem"?',
      [
        'Een leidende bank die zowel schuld als aandelen in een klantonderneming aanhoudt, deze monitort en reddingsacties organiseert',
        'Het postspaarsysteem dat deposito\'s van huishoudens naar publieke investeringen kanaliseert',
        'De window guidance van de centrale bank over de omvang van commerciële kredietverlening',
        'Kruisparticipaties tussen bedrijven binnen een keiretsu-groep',
      ],
      0,
      'Kruisparticipaties zijn een verwant maar apart kenmerk. De monitoringsrol van de hoofdbank is de standaardverklaring voor waarom Japanse bedrijven zo\'n hoge schuldhefboom konden aanhouden.',
    ],
    [
      'Wat werd in 1985 overeengekomen in het Plaza Akkoord?',
      [
        'Gecoördineerde interventie om de Amerikaanse dollar te laten depreciëren, met de grootste gevolgen tegenover de yen',
        'Een terugkeer naar vaste pariteiten tussen de G5-valuta\'s, verdedigd met afgesproken interventiebanden en gezamenlijke reserves',
        'Vrijwillige Japanse exportbeperkingen op auto\'s',
        'Wederzijdse verlagingen van landbouwtarieven tussen de G5',
      ],
      0,
      'De daaropvolgende endaka dreef Japanse fabrikanten naar het buitenland en wordt doorgaans genoemd als voorwaarde voor de binnenlandse activabubbel.',
    ],
    [
      'Wat beschrijft het "vliegende ganzen"-model?',
      [
        'Sequentiële industriële opwaardering, waarbij volwassen industrieën verhuizen naar economieën die zich later ontwikkelen',
        'Gecoördineerd valutabeheer binnen de Oost-Aziatische regio',
        'Exportgeleide groei gefinancierd door onderdrukte huishoudconsumptie',
        'De verspreiding van Japanse managementpraktijken via joint ventures en gelicentieerde technologieoverdracht',
      ],
      0,
      'Akamatsu\'s formulering, nieuw leven ingeblazen door Kojima. De kritiek is dat het een door Japan geleide hiërarchie inleest in wat een veel rommeliger geheel van productienetwerken werd.',
    ],
    [
      'Wat zijn de Zuid-Koreaanse chaebol?',
      [
        'Gediversifieerde bedrijvengroepen onder familiecontrole, doorgaans via kruisparticipaties en holdingstructuren',
        'Staatsbedrijven geprivatiseerd tijdens de liberalisering van de jaren 1980 en vervolgens genoteerd aan de beurs van Seoul',
        'Exportkartels gelicentieerd door het ministerie van Handel',
        'Regionale banken die de zware industrie financierden onder de vijfjarenplannen',
      ],
      0,
      'De crisis van 1997 dwong tot herstructurering van de met schulden gefinancierde diversificatie, maar de zeggenschapsstructuren overleefden dit grotendeels.',
    ],
    [
      'Wat waren township and village enterprises tijdens de Chinese hervormingsperiode?',
      [
        'Plattelandsbedrijven in collectief eigendom, die een groot deel van de industriële groei in de jaren tachtig aandreven',
        'Joint ventures tussen provinciale overheden en buitenlandse investeerders onder het beleid van open kustgebieden',
        'Huishoudelijke bedrijfjes gelicentieerd onder de regels voor individuele onderneming',
        'Staatsfabrieken overgedragen aan de controle van lokale overheden',
      ],
      0,
      'Hun ambigue eigendomsrechten werden lange tijd gezien als een raadsel voor de standaardtheorie - en als bewijs dat particulier eigendomsrecht zeker geen voorwaarde is voor investering.',
    ],
  ],
  phd: [
    [
      'Wat beweert de "balansrecessie"-verklaring van Japans verloren decennium?',
      [
        'Bedrijven gaven voorrang aan schuldaflossing boven investering ondanks bijna-nul rentetarieven, wat monetaire verruiming buiten werking zette',
        'Banken waren insolvent maar verhulden dit, waardoor krediet werd weggehouden van gezonde kredietnemers en toegewezen aan hun bestaande debiteuren',
        'Deflatoire verwachtingen werden zelfvervullend doordat nominale loonrigiditeit de reële lonen liet stijgen tijdens de neerwaartse prijsspiraal',
        'Demografische krimp verlaagde de natuurlijke rentevoet tot onder elk haalbaar beleidsniveau, aansluitend bij Summers\' secular-stagnation-these',
      ],
      0,
      'Koo\'s formulering: met een afwezige bedrijfsvraag naar krediet komt het argument uit bij begrotingsbeleid. De concurrerende "zombiekrediet"-verklaring - Caballero, Hoshi en Kashyap - plaatst het probleem in plaats daarvan bij bankterughoudendheid.',
    ],
    [
      'Met welke gebeurtenis begon de Aziatische financiële crisis van 1997?',
      [
        'Het loslaten van de Thaise baht in juli 1997',
        'De ineenstorting van Hanbo Steel in Zuid-Korea',
        'Indonesië\'s opgave van zijn crawling peg',
        'De verdediging van de Hongkongse valutaraad door de Hong Kong Monetary Authority',
      ],
      0,
      'Het daaropvolgende debat - fundamentals versus zelfvervullende paniek, en of de begrotingsvoorwaarden van het IMF het erger maakten - herschikte de kijk van het vakgebied op kapitaalrekeningliberalisering.',
    ],
    [
      'Wat deed de fiscale hervorming van 1994 (fenshuizhi) in China?',
      [
        'Hercentraliseerde belastinginkomsten naar Peking terwijl de uitgavenverplichtingen bij de lokale overheid bleven',
        'Droeg zowel inkomsten als uitgaven over aan de provincies',
        'Verving het systeem van bedrijfswinstafdracht door een belasting over de toegevoegde waarde tegen ongewijzigde aandelen',
        'Creëerde een formele gemeentelijke obligatiemarkt voor lokale infrastructuur',
      ],
      0,
      'De resulterende kloof tussen inkomsten en uitgaven is de gangbare verklaring voor grondfinanciering en lokale financieringsvehikels van de overheid - en daarmee voor de vastgoedcyclus.',
    ],
  ],
  professor: [
    [
      'Waar stonden de faillissementen van Yamaichi Securities en de Long-Term Credit Bank in 1997-1998 voor?',
      [
        'Het einde van het konvooisysteem, en het begin van echte faillissementen en nationalisatie in Japan',
        'De eerste toepassing van depositoverzekering op Japanse stadsbanken, na de golf van kredietcoöperatie-faillissementen eind jaren tachtig, in plaats van alleen op regionale instellingen',
        'De eerste toepassing van kwantitatieve verruiming door de Bank of Japan, ingevoerd onder gouverneur Hayami om de deflatoire spiraal te doorbreken',
        'De afschaffing van het hoofdbanksysteem bij wet, aangekondigd door het ministerie van Financiën als onderdeel van de Big Bang-hervormingen',
      ],
      0,
      'Tot dan had het ministerie van Financiën reddingsacties georganiseerd zodat geen grote instelling daadwerkelijk failliet ging - precies de manier waarop de verliezen ongezien konden oplopen.',
    ],
  ],
};
